




























import Vue from "vue";
import { authService } from "@/common/services/AuthService";
import { config } from "@/config";
import { accessService } from "@/common/services/AccessService";
import { Paths, RouteInfoMap } from "@/common/enums/RouterPaths";
import { navigationState } from "@/menu/components/NavigationState";
import { printState } from "@/common/state/PrintState";
import { storeService } from "@/common/services/StoreService";
import { DateTime } from "luxon";

interface Destination {
  name: string;
}

interface AppRoute {
  text: string;
  icon: string;
  to: Destination;
  allowed: () => boolean;
}

export default Vue.extend({
  name: "TheAppNavigation",
  data() {
    return {
      authService: authService,
      config,

      navigationState,
      printState,
      storeService,
      printing: false as boolean,
    };
  },
  computed: {
    routes(): AppRoute[] {
      return [
        {
          text: this.$t("modules.menu.components.the-app-navigation.dashboard"),
          icon: "mdi-home-outline",
          to: { name: RouteInfoMap.get(Paths.Dashboard)?.name },
          allowed: (): boolean => accessService.hasAccessToDashboard(),
        },
        {
          text: this.$t("modules.menu.components.the-app-navigation.purchase-reports"),
          icon: "mdi-text-box-check-outline",
          to: { name: RouteInfoMap.get(Paths.PurchaseReports)?.name },
          allowed: (): boolean => accessService.hasAccessToPurchaseReports(),
        },
        {
          text: this.$t("modules.menu.components.the-app-navigation.user-accounts"),
          icon: "mdi-account-outline",
          to: { name: RouteInfoMap.get(Paths.UserAccounts)?.name },
          allowed: (): boolean => accessService.hasAccessToUserAccounts(),
        },
        {
          text: this.$t("modules.menu.components.the-app-navigation.stores"),
          icon: "mdi-store-outline",
          to: {
            name: RouteInfoMap.get(Paths.Stores)?.name,
            query: {
              storeId: storeService.selectedStore,
            },
          },
          allowed: (): boolean => accessService.hasAccessToStores(),
        },
        {
          text: this.$t("modules.menu.components.the-app-navigation.receipts"),
          icon: "mdi-receipt-text-outline",
          to: {
            name: RouteInfoMap.get(Paths.Receipts)?.name,
            query: {
              storeId: storeService.selectedStore,
            },
          },
          allowed: (): boolean => accessService.hasAccessToAllReceipts(),
        },
        {
          text: this.$t("modules.menu.components.the-app-navigation.store-receipts"),
          icon: "mdi-receipt-text-outline",
          to: {
            name: RouteInfoMap.get(Paths.StoreReceipts)?.name,
            query: {
              storeId: storeService.selectedStore,
            },
          },
          allowed: (): boolean => accessService.hasAccessToStoreReceipts(),
        },
        {
          text: this.$t("modules.menu.components.the-app-navigation.products"),
          icon: "mdi-food-apple-outline",
          to: { name: RouteInfoMap.get(Paths.Products)?.name },
          allowed: (): boolean => accessService.hasAccessToProducts(),
        },
        {
          text: this.$t("modules.menu.components.the-app-navigation.data-uploader"),
          icon: "mdi-folder-upload-outline",
          to: { name: RouteInfoMap.get(Paths.DataUploader)?.name },
          allowed: (): boolean => accessService.hasAccessToDataUploader(),
        },
        {
          text: this.$t("modules.menu.components.the-app-navigation.offers"),
          icon: "mdi-tag-outline",
          to: { name: RouteInfoMap.get(Paths.Offers)?.name },
          allowed: (): boolean => accessService.hasAccessToOffers(),
        },
        {
          text: this.$t("modules.menu.components.the-app-navigation.allergens"),
          icon: "mdi-allergy",
          to: { name: RouteInfoMap.get(Paths.Allergens)?.name },
          allowed: (): boolean => accessService.hasAccessToAllergens(),
        },
        {
          text: this.$t("modules.menu.components.the-app-navigation.seasonal-header"),
          icon: "mdi-snowflake",
          to: { name: RouteInfoMap.get(Paths.SeasonalHeader)?.name },
          allowed: (): boolean => accessService.hasAccessToSeasonalHeader(),
        },
        {
          text: this.$t("modules.menu.components.the-app-navigation.product-location"),
          icon: "mdi-map-marker-outline",
          to: { name: RouteInfoMap.get(Paths.ProductLocation)?.name },
          allowed: (): boolean => accessService.hasAccessToProductLocations(),
        },
        {
          text: this.$t("modules.menu.components.the-app-navigation.click-and-collect-orders"),
          icon: "mdi-cart-variant",
          to: {
            name: RouteInfoMap.get(Paths.ClickAndCollect)?.name,
            query: {
              storeId: storeService.selectedStore,
              date: DateTime.now().toFormat("yyyy-MM-dd"),
            },
          },
          allowed: (): boolean => accessService.hasAccessToPickingPortal(),
        },
        {
          text: this.$t("modules.menu.components.the-app-navigation.user-administration"),
          icon: "mdi-account-supervisor",
          to: { name: RouteInfoMap.get(Paths.UserAdministration)?.name },
          allowed: (): boolean => accessService.hasAccessToUserAdministration(),
        },
        {
          text: this.$t("modules.menu.components.the-app-navigation.aisle-category"),
          icon: "mdi-domain",
          to: { name: RouteInfoMap.get(Paths.AisleCategory)?.name },
          allowed: (): boolean => accessService.hasAccessToAisleCategory(),
        },
        {
          text: this.$t("modules.menu.components.the-app-navigation.about"),
          icon: "mdi-information-outline",
          to: { name: RouteInfoMap.get(Paths.About)?.name },
          allowed: (): boolean => authService.isLoggedIn(),
        },
      ] as AppRoute[];
    },
    allowedRoutes(): AppRoute[] {
      return this.routes.filter((route) => route.allowed());
    },
    drawerOpen: {
      get: (): boolean => {
        if (printState.isPrinting) {
          return false;
        } else {
          return navigationState.isDrawerOpen;
        }
      },
      set: (newValue: boolean): void => {
        navigationState.isDrawerOpen = newValue;
      },
    },
    navDrawerPermanent(): boolean {
      if (printState.isPrinting) {
        return false;
      } else {
        return !this.$vuetify.breakpoint.smAndDown;
      }
    },
  },
  methods: {
    async onClickLogout(): Promise<void> {
      await authService.logout();
      await this.$router
        .push({
          name: RouteInfoMap.get(Paths.Dashboard)?.name,
        })
        .catch(() => undefined);
    },
  },
});
